import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaArrowRight, 
  FaBars, 
  FaTimes, 
  FaMoon, 
  FaSun, 
  FaGlobe,
  FaChevronDown
} from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = ({ activeSection, scrollToSection }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const { currentTheme, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  
  // Default implementation of scrollToSection if not provided as a prop
  const safeScrollToSection = (sectionId) => {
    if (typeof scrollToSection === 'function') {
      scrollToSection(sectionId);
    } else {
      // Fallback implementation
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };
  
  // Языки
  const languages = [
    { code: 'ru', label: 'Русский' },
    { code: 'en', label: 'English' }
  ];
  
  // Отслеживание скролла для правильного отображения индикатора активной секции
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Получение пунктов навигационного меню
  const getNavigationItems = () => {
    if (currentLanguage === 'en') {
      return [
        { id: 'home', label: 'Home' },
        { id: 'about', label: 'About' },
        { id: 'services', label: 'Services' },
        { id: 'tech-stack', label: 'Tech Stack' },
        { id: 'work-process', label: 'Work Process' },
        { id: 'contact', label: 'Contact' },
      ];
    }
    
    return [
      { id: 'home', label: 'Главная' },
      { id: 'about', label: 'О нас' },
      { id: 'services', label: 'Услуги' },
      { id: 'tech-stack', label: 'Технологии' },
      { id: 'work-process', label: 'Процесс работы' },
      { id: 'contact', label: 'Контакты' },
    ];
  };
  
  const navigationItems = getNavigationItems();
  
  // Получение пунктов сервисов
  const getServicesItems = () => {
    if (currentLanguage === 'en') {
      return [
        { id: 'web-development', label: 'Web Development', icon: '🌐' },
        { id: 'mobile-apps', label: 'Mobile Apps', icon: '📱' },
        { id: 'ui-ux-design', label: 'UI/UX Design', icon: '🎨' },
        { id: 'branding', label: 'Branding', icon: '✒️' },
        { id: 'seo', label: 'SEO Optimization', icon: '🔍' }
      ];
    }
    
    return [
      { id: 'web-development', label: 'Веб-разработка', icon: '🌐' },
      { id: 'mobile-apps', label: 'Мобильные приложения', icon: '📱' },
      { id: 'ui-ux-design', label: 'UI/UX Дизайн', icon: '🎨' },
      { id: 'branding', label: 'Брендинг', icon: '✒️' },
      { id: 'seo', label: 'SEO Оптимизация', icon: '🔍' }
    ];
  };
  
  const servicesItems = getServicesItems();
  
  // Словарь переводов интерфейса
  const translations = {
    ru: {
      startProject: 'Начать проект',
      lightTheme: 'Светлая тема',
      darkTheme: 'Темная тема'
    },
    en: {
      startProject: 'Start a Project',
      lightTheme: 'Light Theme',
      darkTheme: 'Dark Theme'
    }
  };
  
  // Получение текста на текущем языке
  const getText = (key) => translations[currentLanguage]?.[key] || translations.ru[key];
  
  // Функция для прокрутки к секции услуг и закрытию меню
  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      const yOffset = -100; // Поднять вверх на 100px от верха элемента
      const y = servicesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({top: y, behavior: 'smooth'});
    }
    setIsMobileMenuOpen(false);
  };
  
  // Настройки цветовой темы интерфейса
  const themeClasses = {
    nav: currentTheme === 'light' 
      ? `${isScrolled ? 'bg-white/90 shadow-md' : 'bg-transparent'}`
      : `${isScrolled ? 'bg-[#050505]/90 backdrop-blur-md shadow-lg' : 'bg-transparent'}`,
    logo: currentTheme === 'light'
      ? 'from-light-primary to-light-secondary'
      : 'from-dark-primary to-dark-secondary',
    menuItem: () => currentTheme === 'light'
      ? 'text-gray-700 hover:text-light-primary hover:relative hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:w-full hover:after:h-0.5 hover:after:bg-gradient-to-r hover:after:from-light-primary hover:after:to-light-secondary'
      : 'text-gray-400 hover:text-white hover:relative hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:w-full hover:after:h-0.5 hover:after:bg-gradient-to-r hover:after:from-dark-primary hover:after:to-dark-secondary',
    indicator: currentTheme === 'light'
      ? 'from-light-primary to-light-secondary'
      : 'from-dark-primary to-dark-secondary',
    button: currentTheme === 'light'
      ? 'from-light-primary to-light-secondary hover:shadow-blue-300/20'
      : 'from-dark-primary to-dark-secondary hover:shadow-dark-primary/20',
    iconButton: currentTheme === 'light'
      ? 'bg-gray-100 text-gray-700 hover:text-light-primary'
      : 'bg-[#111] text-gray-400 hover:text-white',
    mobileMenu: currentTheme === 'light'
      ? 'bg-white/95 backdrop-blur-md'
      : 'bg-[#050505]/95 backdrop-blur-md',
    mobileMenuItem: () => currentTheme === 'light'
      ? 'text-gray-700 hover:text-light-primary hover:bg-gray-50 hover:border-l-4 hover:border-light-primary' 
      : 'text-gray-400 hover:text-white hover:bg-[#111] hover:border-l-4 hover:border-dark-primary'
  };

  return (
    <>
      <nav 
        className={`fixed w-full top-0 left-0 z-50 transition-all duration-300 ${themeClasses.nav} ${
          isScrolled ? 'py-3' : 'py-5'
        }`}
      >
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center">
            {/* Логотип */}
            <Link 
              to="/" 
              className={`text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r ${themeClasses.logo} flex items-center`}
            >
              MoviBobby
            </Link>
            
            {/* Десктопное меню */}
            <div className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <div key={item.id} className="relative">
                  <button
                    onClick={() => {
                      safeScrollToSection(item.id);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`relative px-4 py-2 rounded-lg mx-1 transition-all duration-300 ${
                      activeSection === item.id 
                        ? currentTheme === 'light'
                          ? 'text-light-primary relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-light-primary after:to-light-secondary'
                          : 'text-white relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-gradient-to-r after:from-dark-primary after:to-dark-secondary'
                        : themeClasses.menuItem()
                    }`}
                  >
                    {item.label}
                  </button>
                </div>
              ))}
            </div>
            
            {/* Правая группа кнопок (тема, язык, CTA) */}
            <div className="hidden lg:flex items-center space-x-4">
              {/* Переключатель темы */}
              <button 
                onClick={toggleTheme} 
                className={`p-2 rounded-full ${themeClasses.iconButton} transition-colors`}
                aria-label={currentTheme === 'dark' ? 'Включить светлую тему' : 'Включить темную тему'}
              >
                {currentTheme === 'dark' ? <FaSun /> : <FaMoon />}
              </button>
              
              {/* Переключатель языка */}
              <div className="relative dropdown-toggle">
                <button 
                  onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                  className={`p-2 rounded-full ${themeClasses.iconButton} transition-colors flex items-center`}
                >
                  <FaGlobe />
                </button>
                
                {isLanguageDropdownOpen && (
                  <div className={`absolute right-0 mt-2 rounded-lg shadow-lg py-2 w-40 ${currentTheme === 'light' ? 'bg-white border border-gray-100' : 'bg-[#111] border border-[#222]'}`}>
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        className={`block w-full px-4 py-2 text-left ${
                          lang.code === currentLanguage
                            ? currentTheme === 'light' ? 'text-light-primary font-medium' : 'text-dark-primary font-medium'
                            : currentTheme === 'light' ? 'text-gray-700 hover:text-light-primary' : 'text-gray-400 hover:text-white'
                        } ${currentTheme === 'light' ? 'hover:bg-gray-50' : 'hover:bg-[#1a1a1a]'} transition-colors`}
                        onClick={() => {
                          changeLanguage(lang.code);
                          setIsLanguageDropdownOpen(false);
                        }}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Кнопка "Начать проект" */}
              <a 
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  safeScrollToSection('contact');
                }}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white bg-gradient-to-r ${themeClasses.button} hover:shadow-lg transition-all duration-300`}
              >
                {getText('startProject')}
                <FaArrowRight className="text-sm" />
              </a>
            </div>
            
            {/* Мобильное меню */}
            <div className="lg:hidden flex items-center space-x-4">
              {/* Переключатель темы (мобильный) */}
              <button 
                onClick={toggleTheme} 
                className={`p-2 rounded-full ${themeClasses.iconButton} transition-colors`}
                aria-label={currentTheme === 'dark' ? 'Включить светлую тему' : 'Включить темную тему'}
              >
                {currentTheme === 'dark' ? <FaSun className="text-sm" /> : <FaMoon className="text-sm" />}
              </button>
              
              {/* Кнопка открытия меню */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-full ${themeClasses.iconButton} transition-colors`}
              >
                {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
            </div>
          </div>
        </div>
      </nav>
      
      {/* Мобильное меню (оверлей) */}
      {isMobileMenuOpen && (
        <div className={`fixed inset-0 z-40 lg:hidden ${themeClasses.mobileMenu}`} style={{ top: '60px' }}>
          <div className="container mx-auto px-4 py-4">
            <div className="flex flex-col space-y-4">
              {navigationItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => {
                    safeScrollToSection(item.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    activeSection === item.id
                      ? currentTheme === 'light'
                        ? 'text-light-primary bg-gray-50 border-l-4 border-light-primary'
                        : 'text-white bg-[#111] border-l-4 border-dark-primary'
                      : themeClasses.mobileMenuItem()
                  }`}
                >
                  {item.label}
                </button>
              ))}
              
              {/* Кнопка "Начать проект" (мобильная) */}
              <a 
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  safeScrollToSection('contact');
                  setIsMobileMenuOpen(false);
                }}
                className={`mt-4 flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-white bg-gradient-to-r ${themeClasses.button} hover:shadow-lg transition-all duration-300`}
              >
                {getText('startProject')}
                <FaArrowRight className="text-sm" />
              </a>
              
              {/* Переключатель языка (мобильный) */}
              <div className="mt-4 flex flex-col space-y-2 p-4 rounded-lg border border-gray-100 dark:border-[#222]">
                <span className={currentTheme === 'light' ? 'text-gray-500' : 'text-gray-400'}>
                  {currentLanguage === 'en' ? 'Select Language' : 'Выбрать язык'}
                </span>
                <div className="flex space-x-2">
                  {languages.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`px-3 py-1 rounded-md ${
                        lang.code === currentLanguage
                          ? currentTheme === 'light'
                            ? 'bg-light-primary/10 text-light-primary font-medium'
                            : 'bg-dark-primary/10 text-dark-primary font-medium'
                          : currentTheme === 'light'
                            ? 'bg-gray-100 text-gray-700 hover:text-light-primary'
                            : 'bg-[#111] text-gray-400 hover:text-white'
                      }`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar; 


